import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Server, Users, Package, UserCog, Receipt,
  Bell, Moon, Sun, LogOut, Menu, Radio, ChevronRight, Link2, LayoutTemplate, Settings, BarChart3, AppWindow, Megaphone, Cloud,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "reseller", "customer"] },
  { to: "/panels", label: "Panels", icon: Server, roles: ["super_admin"] },
  { to: "/templates", label: "Templates", icon: LayoutTemplate, roles: ["super_admin", "reseller"] },
  { to: "/link-pool", label: "Link Pool", icon: Link2, roles: ["super_admin", "reseller"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["super_admin", "reseller"] },
  { to: "/applications", label: "Applications", icon: AppWindow, roles: ["super_admin", "reseller"] },
  { to: "/plans", label: "Plans", icon: Package, roles: ["super_admin", "reseller"] },
  { to: "/resellers", label: "Resellers", icon: UserCog, roles: ["super_admin"] },
  { to: "/announcements", label: "Announcements", icon: Megaphone, roles: ["super_admin"] },
  { to: "/external-provider", label: "External Provider", icon: Cloud, roles: ["super_admin"] },
  { to: "/sales", label: "Sales History", icon: Receipt, roles: ["super_admin", "reseller"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["super_admin", "reseller"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["super_admin", "reseller", "customer"] },
];

const ROLE_LABEL = { super_admin: "Super Admin", reseller: "Reseller", customer: "Customer" };

function Sidebar({ user, open }) {
  const items = NAV.filter((n) => n.roles.includes(user.role));
  return (
    <aside
      className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-40 h-full w-64 shrink-0 border-r border-border bg-card transition-transform duration-200`}
      data-testid="sidebar"
    >
      <div className="flex items-center gap-2.5 px-6 h-16 border-b border-border">
        <div className="grid place-items-center h-9 w-9 rounded-md bg-primary text-primary-foreground">
          <Radio className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="leading-none">
          <p className="font-display font-bold text-lg tracking-tight">StreamCore</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">IPTV Control</p>
        </div>
      </div>
      <ScrollArea className="h-[calc(100%-4rem)]">
        <nav className="p-3 space-y-1">
          <p className="px-3 pt-3 pb-2 text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Menu</p>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className="h-4.5 w-4.5 h-5 w-5" aria-hidden="true" />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" aria-hidden="true" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </ScrollArea>
    </aside>
  );
}

function NotificationBell() {
  const [notifs, setNotifs] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications || []);
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell className="h-5 w-5" aria-hidden="true" />
          {notifs.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 grid place-items-center h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
              {notifs.length}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-popover">
        <DropdownMenuLabel className="flex items-center justify-between">
          Notifications <Badge variant="secondary">{notifs.length}</Badge>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {notifs.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">You're all caught up.</p>
          ) : (
            notifs.map((n) => (
              <div key={n.id} className="px-3 py-2.5 hover:bg-secondary" data-testid={`notif-${n.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${n.type === "expired" ? "bg-destructive" : "bg-warning"}`} />
                  <p className="text-sm font-medium">{n.title}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 pl-4">{n.message}</p>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user.name || user.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <Sidebar user={user} open={sidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="flex items-center justify-between h-16 px-4 sm:px-6 border-b border-border bg-card/80 backdrop-blur">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen((o) => !o)} data-testid="menu-toggle">
              <Menu className="h-5 w-5" />
            </Button>
            <Badge variant="outline" className="hidden sm:inline-flex border-accent/40 text-accent">
              {ROLE_LABEL[user.role]}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5">
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              <span className="sr-only">Toggle theme</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-2" data-testid="user-menu">
                  <span className="grid place-items-center h-8 w-8 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                    {initials}
                  </span>
                  <span className="hidden sm:block text-sm font-medium max-w-32 truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                <DropdownMenuLabel>
                  <p className="font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground font-normal truncate">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                  <Settings className="h-4 w-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive" data-testid="logout-button">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
